import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// third party
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

// project import
import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import useAuth from 'hooks/useAuth';
import useScriptRef from 'hooks/useScriptRef';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { checkPasswordResetAvailability } from 'utils/passwordResetBlacklist';

// ============================|| COGNITO - FORGOT PASSWORD ||============================ //

interface ForgotPasswordValues {
    email: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AuthForgotPassword = () => {
    const scriptedRef = useScriptRef();
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { sendResetPasswordCode } = useAuth();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<ForgotPasswordValues>({
        mode: 'onBlur',
        defaultValues: { email: '' },
    });

    const onSubmit = async ({ email }: ForgotPasswordValues) => {
        setSubmitError(null);
        try {
            // Domains that authenticate through Microsoft SSO must not go through the Cognito
            // reset flow. The check fails closed, so an inconclusive result also stops here — but
            // it says so instead of claiming the domain is blocked.
            const availability = await checkPasswordResetAvailability(email);
            if (availability !== 'allowed') {
                if (scriptedRef.current) {
                    setSubmitError(
                        t(
                            availability === 'blocked'
                                ? 'auth.password-reset-blocked-use-sso'
                                : 'auth.password-reset-check-unavailable'
                        )
                    );
                }
                return;
            }
            await sendResetPasswordCode(email);
            successAlert(t('auth.password-reset-potentially-sent'));
            setTimeout(() => {
                navigate(email ? `/code-verification?to=${encodeURIComponent(email)}` : '/code-verification', {
                    replace: true,
                });
            }, 1500);
        } catch (err) {
            console.error(err);
            if (scriptedRef.current && err instanceof Error) {
                setSubmitError(err.message);
            }
        }
    };

    const emailRegister = register('email', {
        required: t('auth.email-required'),
        maxLength: { value: 255, message: t('auth.email-too-long') },
        pattern: { value: EMAIL_REGEX, message: t('auth.must-be-valid-email') },
    });

    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
            }}
            noValidate
        >
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <Label htmlFor="email-forgot">
                        <FormattedMessage id="auth.email-address" />
                    </Label>
                    <Input
                        autoComplete="on"
                        id="email-forgot"
                        type="email"
                        placeholder={t('auth.enter-email')}
                        aria-invalid={Boolean(errors.email)}
                        {...emailRegister}
                    />
                    {errors.email && <FieldError>{errors.email.message}</FieldError>}
                </div>
                {submitError && <FieldError>{submitError}</FieldError>}
                <span className="text-caption -mb-4">
                    <FormattedMessage id="auth.do-not-forgot-to-check-spam" />
                </span>
                <Button
                    {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'w-full')}
                    disabled={isSubmitting}
                    type="submit"
                >
                    <FormattedMessage id="auth.send-password-reset-email" />
                </Button>
            </div>
        </form>
    );
};

export default AuthForgotPassword;
